/** Maintained finite counter-service game. The simulation owns recipes,
 * inventory, timing and scoring; renderers and input devices only name public
 * stations. */
import {
  advanceServiceRecipe,
  availableServiceSteps,
  createServiceRecipe,
  expireServiceRecipe,
  type ServiceRecipeProgress,
  type ServiceRecipeStep,
} from './serviceRecipe.js';
import {
  consumeServiceCarry,
  createServiceCarry,
  pickServiceCarry,
  replaceServiceCarry,
  type ServiceCarryState,
} from './serviceFlow.js';

export type ServiceGameVariantV1 = 'standard' | 'alternate';
export type ServiceGameStationIdV1 = 'stock-a' | 'stock-b' | 'stock-c' | 'prep' | 'cook' | 'serve';
export type ServiceGameCommandV1 =
  | { type: 'focus'; station: ServiceGameStationIdV1 }
  | { type: 'activate'; station: ServiceGameStationIdV1 };
export interface ServiceGameStationConfigV1 {
  id: ServiceGameStationIdV1; kind: 'stock' | 'prep' | 'cook' | 'serve';
  x: number; y: number; w: number; h: number; item: string | null; stock: number | null;
}
export interface ServiceGameOrderConfigV1 {
  id: string; label: string; requiredItem: string; stockStation: 'stock-a' | 'stock-b' | 'stock-c';
  patienceTicks: number; score: number; steps: ServiceRecipeStep[];
}
export interface ServiceGameConfigV1 {
  profile: 'service-game@1'; variant: ServiceGameVariantV1;
  world: { width: number; height: number };
  rules: { timeLimitTicks: number; mistakeLimit: number };
  stations: ServiceGameStationConfigV1[];
  orders: ServiceGameOrderConfigV1[];
}
export interface ServiceGameSnapshotV1 {
  tick: number; variant: ServiceGameVariantV1; score: number;
  phase: 'active' | 'won' | 'lost'; lossCause: null | 'mistakes' | 'resources' | 'time';
  timeLeft: number; mistakes: number; mistakeLimit: number; focus: ServiceGameStationIdV1;
  stations: Array<ServiceGameStationConfigV1 & { available: boolean }>;
  carry: string | null;
  activeOrder: null | {
    id: string; label: string; requiredItem: string; deadlineTick: number;
    status: 'active' | 'ready'; completed: string[]; available: string[];
  };
  orders: Array<{ id: string; label: string; status: 'pending' | 'active' | 'served'; requiredItem: string }>;
  servedIds: string[]; totalOrders: number;
}
export type ServiceGameEventV1 =
  | { kind: 'order'; tick: number; data: { orderId: string; label: string; deadlineTick: number } }
  | { kind: 'focus'; tick: number; data: { station: ServiceGameStationIdV1 } }
  | { kind: 'pick'; tick: number; data: { station: ServiceGameStationIdV1; item: string; stock: number } }
  | { kind: 'prep' | 'cook'; tick: number; data: { orderId: string } }
  | { kind: 'serve'; tick: number; data: { orderId: string; score: number } }
  | { kind: 'mistake'; tick: number; data: { station: ServiceGameStationIdV1; mistakes: number } }
  | { kind: 'won'; tick: number; data: { score: number } }
  | { kind: 'lost'; tick: number; data: { cause: 'mistakes' | 'resources' | 'time'; score: number } };

const stationLayout: Omit<ServiceGameStationConfigV1, 'item' | 'stock'>[] = [
  { id:'stock-a', kind:'stock', x:2, y:3, w:2.2, h:2 },
  { id:'stock-b', kind:'stock', x:5.2, y:3, w:2.2, h:2 },
  { id:'stock-c', kind:'stock', x:8.4, y:3, w:2.2, h:2 },
  { id:'prep', kind:'prep', x:3.2, y:7, w:2.5, h:2 },
  { id:'cook', kind:'cook', x:8, y:7, w:2.5, h:2 },
  { id:'serve', kind:'serve', x:13.2, y:5, w:2.5, h:4 },
];
const variants = {
  standard: {
    stock: [['grain',2],['berry',2],['herb',2]] as const,
    orders: [['morning-bun','Morning Bun','grain','stock-a'],['berry-tart','Berry Tart','berry','stock-b'],['herb-pie','Herb Pie','herb','stock-c']] as const,
  },
  alternate: {
    stock: [['cocoa',2],['citrus',2],['tea',2]] as const,
    orders: [['tea-cake','Tea Cake','tea','stock-c'],['cocoa-roll','Cocoa Roll','cocoa','stock-a'],['citrus-slice','Citrus Slice','citrus','stock-b']] as const,
  },
};

/** Returns one of exactly two maintained data variants, always detached. */
export function serviceGameConfigV1(variant: ServiceGameVariantV1 = 'standard'): ServiceGameConfigV1 {
  if (variant !== 'standard' && variant !== 'alternate') throw new RangeError('Unsupported service game variant');
  const data = variants[variant];
  const stations = stationLayout.map((station, index): ServiceGameStationConfigV1 => ({
    ...station, item: station.kind === 'stock' ? data.stock[index]![0] : null,
    stock: station.kind === 'stock' ? data.stock[index]![1] : null,
  }));
  const orders = data.orders.map(([id,label,requiredItem,stockStation]): ServiceGameOrderConfigV1 => ({
    id, label, requiredItem, stockStation, patienceTicks:150, score:100,
    steps: [
      { id:'pick', station:stockStation, choice:requiredItem, requires:[] },
      { id:'prep', station:'prep', choice:'prepare', requires:['pick'] },
      { id:'cook', station:'cook', choice:'finish', requires:['prep'] },
    ],
  }));
  return structuredClone({ profile:'service-game@1', variant, world:{width:16,height:10},
    rules:{timeLimitTicks:520,mistakeLimit:5}, stations, orders });
}

function object(value: unknown, keys: readonly string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype,null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(descriptor => !('value' in descriptor))) {
    throw new RangeError('Invalid service game record');
  }
}
function finite(value: unknown, minimum: number, maximum: number, integer = false): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum
    || integer && !Number.isSafeInteger(value)) throw new RangeError('Invalid service game numeric bound');
}
const validStationIds = new Set<ServiceGameStationIdV1>(['stock-a','stock-b','stock-c','prep','cook','serve']);
function identifier(value: unknown): asserts value is string {
  if (typeof value !== 'string' || !/^[a-z][a-z0-9-]{0,39}$/.test(value)) throw new RangeError('Invalid service game identifier');
}

export function validateServiceGameConfigV1(input: unknown): ServiceGameConfigV1 {
  object(input,['profile','variant','world','rules','stations','orders']);
  if (input.profile !== 'service-game@1' || !['standard','alternate'].includes(String(input.variant))) throw new RangeError('Unsupported service game profile');
  object(input.world,['width','height']); finite(input.world.width,12,24); finite(input.world.height,8,16);
  object(input.rules,['timeLimitTicks','mistakeLimit']); finite(input.rules.timeLimitTicks,120,1800,true); finite(input.rules.mistakeLimit,2,20,true);
  if (!Array.isArray(input.stations) || input.stations.length !== 6) throw new RangeError('Service game requires six stations');
  const stationIds = new Set<string>(), items = new Set<string>();
  for (const station of input.stations) {
    object(station,['id','kind','x','y','w','h','item','stock']);
    if (!validStationIds.has(station.id as ServiceGameStationIdV1) || stationIds.has(String(station.id))) throw new RangeError('Invalid service game station');
    stationIds.add(String(station.id));
    const expectedKind = String(station.id).startsWith('stock-') ? 'stock' : station.id;
    if (station.kind !== expectedKind) throw new RangeError('Service game station kind mismatch');
    finite(station.x,.5,Number(input.world.width)-.5); finite(station.y,.5,Number(input.world.height)-.5);
    finite(station.w,.5,4); finite(station.h,.5,5);
    if (station.kind === 'stock') {
      identifier(station.item); if (items.has(station.item)) throw new RangeError('Duplicate service game stock item'); items.add(station.item);
      finite(station.stock,1,9,true);
    } else if (station.item !== null || station.stock !== null) throw new RangeError('Work stations cannot carry stock');
  }
  if (stationIds.size !== validStationIds.size || [...validStationIds].some(value => !stationIds.has(value))) throw new RangeError('Missing service game station');
  if (!Array.isArray(input.orders) || input.orders.length !== 3) throw new RangeError('Service game requires three orders');
  const orderIds = new Set<string>();
  for (const order of input.orders) {
    object(order,['id','label','requiredItem','stockStation','patienceTicks','score','steps']);
    identifier(order.id); if (orderIds.has(order.id)) throw new RangeError('Duplicate service game order'); orderIds.add(order.id);
    if (typeof order.label !== 'string' || order.label.length < 1 || order.label.length > 48) throw new RangeError('Invalid service game order label');
    identifier(order.requiredItem);
    if (!['stock-a','stock-b','stock-c'].includes(String(order.stockStation))) throw new RangeError('Invalid service game stock station');
    const station = input.stations.find(value => value.id === order.stockStation);
    if (!station || station.item !== order.requiredItem) throw new RangeError('Order stock identity mismatch');
    finite(order.patienceTicks,30,Number(input.rules.timeLimitTicks),true); finite(order.score,1,1000,true);
    if (!Array.isArray(order.steps) || order.steps.length !== 3) throw new RangeError('Service game requires three recipe steps');
    for (const step of order.steps) {
      object(step,['id','station','choice','requires']); identifier(step.id); identifier(step.station); identifier(step.choice);
      if (!Array.isArray(step.requires) || step.requires.some(value => typeof value !== 'string')) throw new RangeError('Invalid service game recipe dependencies');
    }
    const recipe = createServiceRecipe({orderId:order.id,ownerSlot:0,deadlineTick:order.patienceTicks,steps:order.steps as ServiceRecipeStep[]});
    const exact = [
      {id:'pick',station:order.stockStation,choice:order.requiredItem,requires:[]},
      {id:'prep',station:'prep',choice:'prepare',requires:['pick']},
      {id:'cook',station:'cook',choice:'finish',requires:['prep']},
    ];
    if (JSON.stringify(recipe.steps) !== JSON.stringify(exact)) throw new RangeError('Service game recipe is not maintained');
  }
  if (input.orders.reduce((total, order) => total + Number(order.patienceTicks),0) > Number(input.rules.timeLimitTicks)) throw new RangeError('Service game cannot finish all order clocks');
  return structuredClone(input) as unknown as ServiceGameConfigV1;
}

const stationId = (value: unknown): value is ServiceGameStationIdV1 => typeof value === 'string' && validStationIds.has(value as ServiceGameStationIdV1);

export function createServiceGameV1(input: ServiceGameConfigV1) {
  const config = validateServiceGameConfigV1(input);
  let tick = 0, score = 0, mistakes = 0, orderIndex = 0;
  let phase: ServiceGameSnapshotV1['phase'] = 'active', lossCause: ServiceGameSnapshotV1['lossCause'] = null;
  let focus: ServiceGameStationIdV1 = 'stock-a', pending: ServiceGameCommandV1 | null = null;
  let carry: ServiceCarryState = createServiceCarry(1), events: ServiceGameEventV1[] = [];
  const stock = new Map(config.stations.filter(station => station.kind === 'stock').map(station => [station.item!,station.stock!]));
  const served = new Set<string>();
  const makeRecipe = (index: number): ServiceRecipeProgress => {
    const order = config.orders[index]!;
    return createServiceRecipe({orderId:order.id,ownerSlot:0,deadlineTick:tick+order.patienceTicks,steps:order.steps});
  };
  let recipe = makeRecipe(0);
  events.push({kind:'order',tick,data:{orderId:config.orders[0]!.id,label:config.orders[0]!.label,deadlineTick:recipe.deadlineTick}});
  const heldItem = () => carry.carried.find(entry => entry.slot === 0)?.item ?? null;
  const currentOrder = () => config.orders[orderIndex]!;
  const lose = (cause: Exclude<ServiceGameSnapshotV1['lossCause'],null>) => {
    phase='lost'; lossCause=cause; events.push({kind:'lost',tick,data:{cause,score}});
  };
  const mistake = (station: ServiceGameStationIdV1) => {
    mistakes++; events.push({kind:'mistake',tick,data:{station,mistakes}});
    if (mistakes >= config.rules.mistakeLimit) lose('mistakes');
  };
  const impossible = () => {
    if (phase !== 'active') return false;
    const order=currentOrder(), prepDone=recipe.completed.includes('prep');
    return !prepDone && heldItem() !== order.requiredItem && (stock.get(order.requiredItem) ?? 0) === 0;
  };
  const advance = (stepId: 'pick'|'prep'|'cook', station: ServiceGameStationIdV1, choice: string) => {
    const result=advanceServiceRecipe(recipe,{orderId:currentOrder().id,slot:0,stepId,station,choice},tick);
    if (!result.accepted) return false;
    recipe=result.state; return true;
  };
  const activateStock = (station: ServiceGameStationConfigV1) => {
    const item=station.item!, remaining=stock.get(item) ?? 0;
    if (remaining <= 0) { mistake(station.id); return; }
    const order=currentOrder(), prior=heldItem();
    stock.set(item,remaining-1);
    if (prior === null) carry=pickServiceCarry(carry,0,item).state;
    else if (prior !== item) carry=replaceServiceCarry(carry,0,prior,item).state;
    events.push({kind:'pick',tick,data:{station:station.id,item,stock:remaining-1}});
    if (!recipe.completed.includes('pick') && station.id === order.stockStation && item === order.requiredItem) {
      advance('pick',station.id,item); return;
    }
    if (prior === item && item === order.requiredItem) return;
    mistake(station.id);
  };
  const activateWork = (station: ServiceGameStationIdV1) => {
    const order=currentOrder();
    if (station === 'prep') {
      if (heldItem() !== order.requiredItem || !availableServiceSteps(recipe).some(step => step.id === 'prep')) { mistake(station); return; }
      const consumed=consumeServiceCarry(carry,0,order.requiredItem); if (!consumed.accepted) { mistake(station); return; }
      carry=consumed.state; if (!advance('prep','prep','prepare')) { mistake(station); return; }
      events.push({kind:'prep',tick,data:{orderId:order.id}}); return;
    }
    if (station === 'cook') {
      if (!advance('cook','cook','finish')) { mistake(station); return; }
      events.push({kind:'cook',tick,data:{orderId:order.id}}); return;
    }
    if (station === 'serve') {
      if (recipe.status !== 'ready') { mistake(station); return; }
      served.add(order.id); score+=order.score; events.push({kind:'serve',tick,data:{orderId:order.id,score:order.score}});
      orderIndex++;
      if (orderIndex === config.orders.length) { phase='won'; events.push({kind:'won',tick,data:{score}}); return; }
      recipe=makeRecipe(orderIndex);
      const next=currentOrder(); events.push({kind:'order',tick,data:{orderId:next.id,label:next.label,deadlineTick:recipe.deadlineTick}});
    }
  };
  return {
    applyCommand(slot: number, command: unknown): boolean {
      if (slot !== 0 || phase !== 'active') return false;
      try { object(command,['type','station']); } catch { return false; }
      if (!['focus','activate'].includes(String(command.type)) || !stationId(command.station)) return false;
      pending=command as ServiceGameCommandV1; return true;
    },
    step(): void {
      if (phase !== 'active') return;
      tick++;
      const command=pending; pending=null;
      if (command?.type === 'focus') {
        focus=command.station; events.push({kind:'focus',tick,data:{station:focus}});
      } else if (command?.type === 'activate') {
        focus=command.station;
        const station=config.stations.find(value => value.id === command.station)!;
        if (station.kind === 'stock') activateStock(station); else activateWork(station.id);
      }
      if (phase !== 'active') return;
      if (impossible()) { lose('resources'); return; }
      recipe=expireServiceRecipe(recipe,tick);
      if (recipe.status === 'expired' || tick >= config.rules.timeLimitTicks) lose('time');
    },
    snapshot(): ServiceGameSnapshotV1 {
      const active=phase === 'active' ? currentOrder() : null;
      return structuredClone({tick,variant:config.variant,score,phase,lossCause,timeLeft:Math.max(0,config.rules.timeLimitTicks-tick),
        mistakes,mistakeLimit:config.rules.mistakeLimit,focus,
        stations:config.stations.map(station=>({...station,stock:station.item===null?null:stock.get(station.item)??0,
          available:station.item===null||Boolean(stock.get(station.item))})), carry:heldItem(),
        activeOrder:active?{id:active.id,label:active.label,requiredItem:active.requiredItem,deadlineTick:recipe.deadlineTick,
          status:recipe.status as 'active'|'ready',completed:[...recipe.completed],available:availableServiceSteps(recipe).map(step=>step.id)}:null,
        orders:config.orders.map((order,index)=>({id:order.id,label:order.label,requiredItem:order.requiredItem,
          status:served.has(order.id)?'served' as const:index===orderIndex&&phase==='active'?'active' as const:'pending' as const})),
        servedIds:[...served],totalOrders:config.orders.length});
    },
    drainEvents(): ServiceGameEventV1[] { const drained=events; events=[]; return drained; },
  };
}
